<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="isCartOpen" class="fixed top-0 left-0 z-50 w-screen h-screen">
    <div
      class="flex flex-col bg-primary-white z-10 h-full min-h-screen relative w-screen max-w-[626px] float-right"
    >
      <div
        class="flex justify-center items-center bg-black text-primary-white py-[10px] px-5 text-sm gap-x-[10px]"
      >
        <div>{{ $t('cart.promote') }}</div>
        <NuxtLinkLocale class="underline" to="/products" @click="closeCart">{{
          $t('cart.promoteShop')
        }}</NuxtLinkLocale>
      </div>
      <div class="px-5 py-4 md:px-10">
        <div class="flex flex-row items-center justify-between">
          <div class="text-xl font-bold">
            {{ $t('cart.addBag') }} ({{
              items.reduce((acc, item) => {
                return acc + item?.quantity
              }, 0)
            }})
          </div>
          <div class="cursor-pointer" @click="closeCart">
            <Icon name="times" />
          </div>
        </div>
        <div class="flex flex-row items-center justify-between">
          <div class="text-[#636769] text-sm">
            <div v-if="amountToFreeShipping > 0">
              {{ $t('cart.youAre') }}
              {{ displayCommaNumber(amountToFreeShipping) }}
              {{ $t('cart.awayFromFreeShipping') }}
            </div>
            <div v-else>{{ $t('cart.freeShipping') }}</div>
          </div>
          <div
            v-if="amountToFreeShipping > 0"
            class="w-full max-w-[140px] relative"
          >
            <div class="w-full h-[2px] bg-border-divider">
              <div
                class="absolute h-[2px] left-0 bg-black"
                :style="{
                  width: `calc(${(currentAmount / freeShippingAmount) * 100}%)`
                }"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        data-scroll-lock-scrollable
        class="flex flex-col flex-1 overflow-scroll bg-[#F6F5F4] px-5 md:px-10 py-4"
        :style="{
          maxHeight: `${appHeight - (width > 768 ? 292 : 296)}px`
        }"
      >
        <div class="flex flex-col flex-1 gap-y-4 md:gap-y-6 pb-safe">
          <div v-for="item in items" :key="item.cartItemId" class="flex">
            <CartItem
              :key="item.cartItemId"
              :data="item"
              :device-list="deviceList?.data"
              @loaded="() => (loadedItem[item.cartItemId] = true)"
              @delete="() => (loadedItem[item.cartItemId] = false)"
            />
          </div>
          <div
            v-if="items.length > 0"
            class="flex items-center pt-6 font-bold border-t border-dashed border-border-divider gap-x-2"
          >
            <Icon name="multiple-tags-solid" />{{ $t('cart.applyPromoCode') }}
          </div>
        </div>
      </div>
      <div
        ref="continueCheckoutEl"
        class="px-10 py-4 fixed w-full max-w-[626px] bottom-0 bg-primary-white"
      >
        <div class="space-y-4 pb-safe">
          <div class="flex items-center justify-between font-bold">
            <div>{{ $t('cart.total') }}</div>
            <div>{{ displayCommaNumber(currentAmount, 2) }} THB</div>
          </div>
          <div class="space-y-2">
            <Button
              :disabled="items.length === 0 || isLoading"
              full
              :loading="isLoading && size(items) > size(loadedItem)"
              @click="handleContinueToCheckout"
              >{{ $t('cart.btnContinue') }}</Button
            >
            <div class="text-sm text-center text-typo-disabled">
              {{ $t('cart.condition1') }}
              <NuxtLinkLocale
                class="underline"
                to="/terms-and-conditions"
                @click="closeCart"
                >{{ $t('cart.condition2') }}</NuxtLinkLocale
              >
              {{ $t('cart.condition3') }}
              <NuxtLinkLocale
                class="underline"
                to="/privacy-policy"
                @click="closeCart"
              >
                {{ $t('cart.condition4') }}
              </NuxtLinkLocale>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="fixed top-0 left-0 w-screen h-screen bg-black opacity-80"
      @click="closeCart"
    />
  </div>
</template>

<script lang="ts" setup>
import { every, find, size } from 'lodash-es'
import scrollLock from 'scroll-lock'

const router = useRouter()
const localePath = useLocalePath()
const webStore = useWebsiteStore()
const { isCartOpen } = storeToRefs(webStore)
const { closeCart } = useWebsiteStore()
const appHeight = ref<number>(0)

const cartStore = useCartStore()
const { items, itemsData } = storeToRefs(cartStore)
const { width } = useWindowSize()

const userStore = useUserStore()
const { access_token } = storeToRefs(userStore)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const loadedItem = ref<any>({})

const isLoading = computed(
  () => !every(items.value, (item) => loadedItem.value?.[item?.cartItemId])
)

const continueCheckoutEl = ref(null)

onMounted(() => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i) // Get the key at the current index
    if (key) {
      const storedValue = localStorage.getItem(key) // Get the value for that key
      if (storedValue?.startsWith('data:image/png;base64,')) {
        localStorage.removeItem(key)
      }
    }
  }
})

const { data: deviceList } = useQuery<GetDeviceAllResponse>(
  () => `/v1/storefront/personalization/products/all`,
  {
    key: 'get-device-list',
    immediate: true,
    method: 'GET'
  }
)

onUnmounted(() => {
  loadedItem.value = {}
})

const freeShippingAmount = 990
const currentAmount = computed(() => {
  return itemsData.value.reduce((acc, item) => {
    const quantity = find(
      items.value,
      (i) => i.cartItemId === item.cartItemId
    )?.quantity
    return acc + Number(item.price) * (quantity || 0)
  }, 0)
})
const amountToFreeShipping = computed(
  () => freeShippingAmount - currentAmount.value
)

const handleContinueToCheckout = async () => {
  console.log('gogogohandleContinueToCheckout')

  on('begin_checkout', {
    total_value: currentAmount.value,
    currency: 'THB',
    item_id: itemsData.value.map((item) => item.productSku + item.artworkSku)
  })
  if (!access_token.value) {
    await router.push({
      path: localePath(`/sign-in`),
      query: { redirect: '/checkout' }
    })
  } else {
    await router.push({
      path: localePath('/checkout')
    })
  }
  setTimeout(() => closeCart(), 700)
}

const { on } = useEventTracking()

watch(
  () => isCartOpen.value,
  (nextIsOpen) => {
    if (nextIsOpen) {
      on('view_cart', {
        total_value: currentAmount.value,
        item_id: itemsData.value.map(
          (item) => item.productSku + item.artworkSku
        )
      })

      scrollLock.disablePageScroll()
    } else {
      loadedItem.value = {}
      scrollLock.enablePageScroll()
    }
  }
)

const getAppHeight = () => {
  appHeight.value =
    window?.innerHeight || document?.documentElement?.clientHeight
}

onMounted(() => {
  console.log('5555555555555')

  let timer: NodeJS.Timeout
  getAppHeight()
  window?.addEventListener('resize', () => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      getAppHeight()
    }, 200)
  })
})

onUnmounted(() => {
  window?.removeEventListener('resize', getAppHeight)
})
</script>
